import type React from "react";
import { createContext, useContext } from "react";
import { useQueryFeatureFlagsCapabilities } from "src/api/featureFlags/featureFlags";
import { useSettings } from "src/hooks";

/**
 * Define a list of capabilities the user can or cannot do on the application
 */

export type CapabilityKey = keyof Capabilities;
export enum CapabilitiesKey {
    PARALLEL_OFFERS_ENABLED = "parallelOffersEnabled",
    PARALLEL_SPONSORED_ADS_ENABLED = "parallelSponsoredAdsEnabled",
    WELCOME_PAGE_ENABLED = "welcomePagePageEnabled",
    BULK_ACTIONS_ENABLED = "bulkActionsEnabled",
}

export type Capabilities = Record<CapabilitiesKey, boolean>;

interface CapabilitiesContextValue {
    capabilities: Capabilities | null;
    isLoading: boolean;
}

const defaultValue: CapabilitiesContextValue = {
    isLoading: false,
    capabilities: {
        parallelOffersEnabled: false,
        parallelSponsoredAdsEnabled: false,
        welcomePagePageEnabled: false,
        bulkActionsEnabled: false,
    },
};

const CapabilitiesContext = createContext<CapabilitiesContextValue>(defaultValue);

export function CapabilitiesProvider({ children }: { children: React.ReactNode }): JSX.Element {
    const settings = useSettings();
    const { data, isLoading } = useQueryFeatureFlagsCapabilities({
        posId: settings.settings.possIds?.length === 1 ? settings.settings.possIds[0] : undefined,
    });

    return (
        <CapabilitiesContext.Provider
            value={{ capabilities: data ?? defaultValue.capabilities, isLoading }}
        >
            {children}
        </CapabilitiesContext.Provider>
    );
}

export function useCapabilities(): CapabilitiesContextValue {
    return useContext(CapabilitiesContext);
}
